import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Inicio', href: '/' },
    { name: 'Nosotros', href: '/nosotros' },
    { name: 'Servicios', href: '/servicios' },
    { name: 'Destinos', href: '/destinos' },
    { name: 'Beneficios', href: '/beneficios' },
    { name: 'Contacto', href: '/contacto' },
  ];

  const showLightNav = isScrolled || !isHomePage;

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          showLightNav
            ? 'bg-white/95 backdrop-blur-xl shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Original from Brandbook */}
            <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
              <span
                className={`text-2xl font-extrabold tracking-tighter transition-colors ${
                  showLightNav ? 'text-[#1A1A1A]' : 'text-white'
                }`}
              >
                COLLEGE
              </span>
              <span
                className={`text-2xl font-light tracking-tight transition-colors ${
                  showLightNav ? 'text-[#6B7280]' : 'text-white/80'
                }`}
              >
                TRAVELER
              </span>
              <span className="w-2 h-2 rounded-full bg-[#009DE0]"></span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    location.pathname === link.href
                      ? 'bg-[#009DE0] text-white'
                      : showLightNav 
                        ? 'text-[#1A1A1A] hover:text-[#009DE0] hover:bg-gray-100'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="https://wa.me/522282728556"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full font-medium text-sm hover:bg-green-700 transition-all"
              >
                <MessageCircle size={16} />
                <span className="hidden xl:inline">WhatsApp</span>
              </a>
              <Link
                to="/contacto"
                data-testid="navbar-cta"
                className="bg-[#009DE0] text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-[#007BB0] transition-all duration-300 hover:scale-105"
              >
                Cotizar Viaje
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-full transition-colors ${
                showLightNav
                  ? 'text-[#1A1A1A] hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Fixed with solid dark background */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-20 left-0 right-0 bg-[#1A1A1A] border-t border-white/10 shadow-xl"
          >
            <div className="py-6 px-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 px-4 rounded-xl font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'bg-[#009DE0] text-white'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 flex gap-3">
                <a
                  href="https://wa.me/522282728556"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3 rounded-full font-semibold"
                >
                  <MessageCircle size={18} />
                  WhatsApp
                </a>
                <Link
                  to="/contacto"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex-1 bg-[#009DE0] text-white py-3 rounded-full font-semibold text-center"
                >
                  Cotizar
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/522282728556"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 hover:scale-110 transition-transform"
      >
        <MessageCircle size={28} className="text-white" />
      </a>
    </>
  );
};

export default Navbar;
